import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { useBuildingOptions } from '../../common/hooks/useBuildingOptions'
import {
  getInitialTimeStart,
  getInitialTimeEnd,
  defaultFrequencyOption,
  visibilityDefaultValue,
} from '../../common/helpers'
import { mapReminderDtoToFormData } from '../mappers'

const getInitialValues = (timezone) => ({
  // Event details
  building: null,
  name: '',
  description: '',
  image: null,
  files: [],
  // Date/Time
  place: '',
  timeStart: getInitialTimeStart(timezone),
  timeEnd: getInitialTimeEnd(timezone),
  frequency: defaultFrequencyOption,
  // Attendees - Communication
  automatedRemindersToggle: false,
  remindAll: false,
  remindAllValues: [''],
  isPrivate: visibilityDefaultValue,
})

const makeValidationSchema = (t) =>
  Yup.object().shape({
    building: Yup.object()
      .nullable()
      .required(t('eventManagement:reminderFormPage.validation.buildingRequired')),
    name: Yup.string().required(t('eventManagement:reminderFormPage.validation.nameRequired')),
    description: Yup.string().required(
      t('eventManagement:reminderFormPage.validation.descriptionRequired'),
    ),
    image: Yup.object()
      .nullable()
      .required(t('eventManagement:reminderFormPage.validation.photoRequired')),
    location: Yup.string().required(
      t('eventManagement:reminderFormPage.validation.locationRequired'),
    ),
    timeStart: Yup.object().required(
      t('eventManagement:reminderFormPage.validation.dateTimeRequired'),
    ),
  })

export const useReminderFormState = ({ userData, eventData, t }) => {
  const selectedBuildingTimezone = useSelector(selectActiveBuilding)?.timezone

  const { buildingOptions, isLoading: isBuildingOptionsLoading } = useBuildingOptions(userData)

  const initialValues = useMemo(() => {
    return eventData
      ? mapReminderDtoToFormData({
          eventData,
          buildingOptions,
        })
      : getInitialValues(selectedBuildingTimezone)
  }, [eventData, buildingOptions])

  const validationSchema = useMemo(() => makeValidationSchema(t), [t])

  return {
    initialValues,
    validationSchema,
    buildingOptions,
    areOptionsLoading: isBuildingOptionsLoading,
  }
}
